import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { formatDate } from 'utils';
import { CTP_ELGIBILITY_DETAILS } from 'utils/constants';
import { selectEntitlements } from 'services/session/sessionSelectors';

const selectUserState = (state: RootState) => state.user;

export const selectUserId = createSelector(
  [selectUserState],
  (userState) => userState.id,
);

export const selectUserName = createSelector(
  [selectUserState],
  (userState) => userState.name ?? {},
);

export const selectLoginUserName = createSelector(
  [selectUserState],
  (userState) => userState.username,
);

export const selectUserHasName = createSelector(
  [selectUserName],
  (userName) => !!userName.first,
);

export const selectUserDob = createSelector(
  [selectUserState],
  (userState) => userState.birthDate,
);

export const selectUserEmail = createSelector(
  [selectUserState],
  (userState) => userState.emailAddress,
);

export const selectUserLocale = createSelector(
  [selectUserState],
  (userState) => userState.locale,
);

export const selectUserLastVisitedAt = createSelector(
  [selectUserState],
  (userState) => userState.account?.lastVisitedAt,
);

export const selectUserSince = createSelector(
  [selectUserState],
  (userState) => userState.createdAt,
);

export const selectUserAddress = createSelector(
  [selectUserState],
  (userState) => userState.address ?? {},
);

export const selectUserPhoneNumber = createSelector(
  [selectUserState],
  (userState) => userState.phoneNumber,
);

export const selectServiceEndDate = createSelector(
  [selectUserState],
  (userState) => {
    if (userState.serviceEndDate) {
      return formatDate(userState.serviceEndDate, 'MM/DD/YYYY');
    }

    return '';
  },
);

export const selectServiceEndDateLong = createSelector(
  [selectUserState],
  (userState) => {
    if (userState.serviceEndDate) {
      const formatted = formatDate(userState.serviceEndDate, 'MMM D, YYYY');
      return formatted === 'NA' ? '' : formatted;
    }

    return '';
  },
);

export const selectUserLastFourSSN = createSelector(
  [selectUserState],
  (userState) => userState.ssnLastFour,
);

export const selectUserBillable = createSelector(
  [selectUserState],
  (userState) => userState.billable,
);

export const selectUserStatus = createSelector(
  [selectUserState],
  (userState) => userState.status,
);

/** Paid billing UI — billable can be set before status catches up to subscriber. */
export const selectShowConvertedToPaidBilling = createSelector(
  [selectUserBillable, selectUserStatus],
  (billable, status) =>
    Boolean(billable) || status === CTP_ELGIBILITY_DETAILS.subscriber,
);

export const selectUserVerification = createSelector(
  [selectUserState],
  (userState) => userState.verification,
);

export const selectUserHasTUID = createSelector(
  [selectEntitlements],
  (entitlements) => {
    const verification = entitlements['itps:verification'];
    if (Array.isArray(verification)) return verification.includes('tuid');
    if (typeof verification === 'string') return verification === 'tuid';
    return false;
  },
);

export const selectUserHasXID = createSelector(
  [selectEntitlements],
  (entitlements) => {
    const verification = entitlements['itps:verification'];
    if (Array.isArray(verification)) return verification.includes('xid');
    if (typeof verification === 'string') return verification === 'xid';
    return false;
  },
);

export const selectUserHasIDAuthOrEid = createSelector(
  [selectEntitlements],
  (entitlements) => {
    const verification = entitlements['itps:verification'];
    if (Array.isArray(verification))
      return verification.includes('idauth') || verification.includes('eid');
    if (typeof verification === 'string')
      return verification === 'idauth' || verification === 'eid';
    return false;
  },
);

export const selectUserFeatures = createSelector(
  [selectUserState],
  (userState) => userState.features ?? [],
);

export const selectUserMigratedAt = createSelector(
  [selectUserState],
  (userState) => userState.migratedAt,
);

export const selectContactVerificationStatus = createSelector(
  [selectUserState],
  (userState) => userState.contactVerificationStatus ?? null,
);

export const selectAddressSource = createSelector(
  [selectUserState],
  (userState) => userState.addressSource ?? null,
);

export const selectUserSuppliedAddressFromUser = createSelector(
  [selectUserState],
  (userState) => userState.userSuppliedAddress ?? null,
);
